import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import useSound from "use-sound";
import toast from "react-hot-toast";

import Menu from "./Menu/Menu";
import StandingCard from "./StandingCard/StandingCard";
import TopCard from "./TopCard/TopCard";
import Filter from "./Filter/Filter";
import useSize from "../../../../Utils/useSize/useSize";
import Backend from "../../../../Backend/Backend";
import Loading from "../../../../Utils/Loading/Loading";
import NotFound from "../../../../Utils/NotFound/NotFound";
import ErrorSoundFile from "../../../../Sound/Error.wav";

const backend = await new Backend().init();

export default function TradeWise() {
  const dispatch = useDispatch();
  const windowSize = useSize();
  const [playError] = useSound(ErrorSoundFile);

  const userType = Number(localStorage.getItem("user_type") || 7);

  const [loading, setLoading] = useState(false);
  const [standingDelete, setStandingDelete] = useState(false);
  const [positions, setPositions] = useState([]);
  const [allPositions, setAllPositions] = useState([]);
  const [superId, setSuperId] = useState(0);
  const [masterId, setMasterId] = useState(0);
  const [userId, setUserId] = useState(0);

  /** ✅ Build common user data */
  const buildUserData = useCallback(
    (sup = 0, mas = 0, usr = 0) => ({
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      super_id: userType === 2 ? localStorage.getItem("id") : sup,
      master_id: mas,
      user_id: usr,
      trade: "1",
    }),
    []
  );

  /** ✅ Main loader */
  const loadStanding = useCallback(
    async (sup = 0, mas = 0, usr = 0) => {
      setLoading(true);
      try {
        let data = buildUserData(sup, mas, usr);
        let res;

        // 🔹 Correct backend selection based on role
        switch (userType) {
          case 1:
            res = await backend.standing_admin(data);
            break;
          case 2:
            res = await backend.standing_super(data);
            break;
          case 3:
            res = await backend.standing_master(data);
            break;
          case 4:
            res = await backend.standing(data);
            break;
          case 5:
            res = await backend.standing_broker(data);
            break;
          default:
            toast.error("Invalid user type");
            setLoading(false);
            return;
        }

        if (res.error === "False") {
          setPositions(res.standing);
          setAllPositions(res.standing);
          setStandingDelete(res.standing_delete);
          dispatch({ type: "CLEAR_PF" });
        } else {
          if (res.logout === 1) {
            window.LogOut?.();
          } else {
            playError();
            toast.error(res.message || "Failed to load data");
          }
        }
      } catch (err) {
        console.error("Standing Load Error:", err);
        playError();
        toast.error("Failed to load standing data.");
      } finally {
        setLoading(false);
      }
    },
    [userType, buildUserData, dispatch, playError]
  );

  /** 🔹 Initial Load */
  useEffect(() => {
    loadStanding();
  }, [loadStanding]);

  /** 🔹 Called by Filter (Super/Master/User change) */
  const handleLoadData = useCallback(
    (sup, mas, usr) => {
      setSuperId(sup || 0);
      setMasterId(mas || 0);
      setUserId(usr || 0);
      loadStanding(sup, mas, usr);
    },
    [loadStanding]
  );

  /** 🔹 Clear Filters */
  const handleClear = useCallback(() => {
    setSuperId(0);
    setMasterId(0);
    setUserId(0);
    loadStanding(0, 0, 0);
  }, [loadStanding]);

  /** 🔹 Symbol Search */
  const handleSearch = useCallback(
    (text) => {
      if (text.length > 1) {
        setPositions(
          allPositions.filter((x) =>
            x.symbol.toLowerCase().includes(text.toLowerCase())
          )
        );
      } else {
        setPositions(allPositions);
      }
    },
    [allPositions]
  );

  /** 🔹 Manual Reload */
  const handleReload = useCallback(
    () => loadStanding(superId, masterId, userId),
    [loadStanding, superId, masterId, userId]
  );

  /** ✅ Responsive Layout */
  return (
    <div>
      <Filter
        search={handleSearch}
        records={positions.length}
        loadStanding={handleLoadData}
        clear={handleClear}
        reload={handleReload}
        userType={userType}
      />

      <TopCard user_id={userId} userType={userType} standing={positions} />
      <Menu userType={userType} standing_delete={standingDelete} />

      <div
        key={loading ? "loading" : "loaded"}
        style={{
          width: windowSize[0],
          height: windowSize[1] - 265,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <div className="loadingContainer">
            <Loading height={windowSize[1] - 265} />
          </div>
        ) : positions.length > 0 ? (
          <div
            style={{
              width: windowSize[0],
              height: windowSize[1] - 265,
              overflowY: "auto",
            }}
          >
            {positions.map((item, index) => (
              <StandingCard
                key={item.id}
                userType={userType}
                item={item}
                index={index + 1}
                total={positions.length}
                reload={handleReload}
                standing_delete={standingDelete}
              />
            ))}
          </div>
        ) : (
          <NotFound height={windowSize[1] - 265} title="Nothing here" subtext="No Standing Found." />
        )}
      </div>
    </div>
  );
}
